import React, { useState, useRef } from 'react';
import { generateBridgePrompt, processBridgeResponse, BRIDGE_TYPES, BRIDGE_TYPE_LABELS } from '../../services/claudeBridge';
import { THEME } from '../../styles/theme';

const S = {
  container: {
    padding: '20px',
    maxWidth: '100%',
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '24px',
  },
  title: {
    fontSize: '22px',
    fontWeight: '700',
    color: THEME.colors.gold,
    letterSpacing: '1px',
  },
  subtitle: {
    fontSize: '13px',
    color: THEME.colors.textMuted,
    marginTop: '2px',
  },
  stepRow: {
    display: 'flex',
    gap: '8px',
    marginBottom: '24px',
    flexWrap: 'wrap',
  },
  step: (active) => ({
    padding: '6px 14px',
    borderRadius: '20px',
    fontSize: '13px',
    fontWeight: '600',
    background: active ? THEME.colors.blue : 'rgba(255,255,255,0.06)',
    color: active ? '#000' : THEME.colors.textMuted,
    transition: 'all 0.2s',
  }),
  section: {
    background: 'rgba(255,255,255,0.04)',
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '16px',
    border: `1px solid rgba(255,255,255,0.08)`,
  },
  sectionTitle: {
    fontSize: '13px',
    fontWeight: '700',
    color: THEME.colors.textMuted,
    textTransform: 'uppercase',
    letterSpacing: '1.5px',
    marginBottom: '14px',
  },
  typeGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))',
    gap: '10px',
  },
  typeBtn: (selected) => ({
    padding: '12px 14px',
    borderRadius: '10px',
    border: selected
      ? `2px solid ${THEME.colors.blue}`
      : '2px solid rgba(255,255,255,0.08)',
    background: selected ? 'rgba(0,212,255,0.12)' : 'rgba(255,255,255,0.03)',
    color: selected ? THEME.colors.blue : THEME.colors.text,
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'all 0.2s',
  }),
  typeEmoji: {
    fontSize: '20px',
    display: 'block',
    marginBottom: '6px',
  },
  input: {
    width: '100%',
    padding: '12px',
    background: 'rgba(255,255,255,0.06)',
    border: `1px solid rgba(255,255,255,0.12)`,
    borderRadius: '8px',
    color: THEME.colors.text,
    fontSize: '14px',
    resize: 'vertical',
    minHeight: '70px',
    outline: 'none',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
  },
  promptBox: {
    width: '100%',
    padding: '14px',
    background: 'rgba(0,0,0,0.4)',
    border: `1px solid rgba(0,212,255,0.3)`,
    borderRadius: '8px',
    color: THEME.colors.text,
    fontSize: '13px',
    resize: 'vertical',
    minHeight: '200px',
    outline: 'none',
    fontFamily: 'monospace',
    boxSizing: 'border-box',
    lineHeight: '1.6',
  },
  pasteBox: {
    width: '100%',
    padding: '14px',
    background: 'rgba(0,0,0,0.4)',
    border: `1px solid rgba(0,255,136,0.3)`,
    borderRadius: '8px',
    color: THEME.colors.text,
    fontSize: '13px',
    resize: 'vertical',
    minHeight: '180px',
    outline: 'none',
    fontFamily: 'monospace',
    boxSizing: 'border-box',
    lineHeight: '1.6',
  },
  btnRow: {
    display: 'flex',
    gap: '10px',
    flexWrap: 'wrap',
  },
  btn: (variant) => ({
    padding: '10px 20px',
    borderRadius: '8px',
    border: 'none',
    fontWeight: '700',
    fontSize: '14px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'all 0.2s',
    background: variant === 'primary'   ? THEME.colors.blue
              : variant === 'success'   ? THEME.colors.green
              : variant === 'gold'      ? THEME.colors.gold
              : 'rgba(255,255,255,0.1)',
    color: variant === 'ghost' ? THEME.colors.text : '#000',
    opacity: 1,
  }),
  savedBanner: {
    background: 'rgba(0,255,136,0.1)',
    border: `1px solid rgba(0,255,136,0.3)`,
    borderRadius: '10px',
    padding: '16px 20px',
    marginBottom: '16px',
  },
  savedTitle: {
    fontSize: '15px',
    fontWeight: '700',
    color: THEME.colors.green,
    marginBottom: '6px',
  },
  savedDetail: {
    fontSize: '13px',
    color: THEME.colors.text,
  },
  savedPills: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
    marginTop: '10px',
  },
  pill: (color) => ({
    padding: '4px 12px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '700',
    background: `${color}22`,
    color: color,
    border: `1px solid ${color}44`,
  }),
  errorBanner: {
    background: 'rgba(255,51,51,0.1)',
    border: `1px solid rgba(255,51,51,0.3)`,
    borderRadius: '10px',
    padding: '14px 18px',
    color: THEME.colors.red,
    fontSize: '13px',
    marginBottom: '16px',
  },
  hint: {
    fontSize: '12px',
    color: THEME.colors.textMuted,
    marginTop: '8px',
    lineHeight: '1.6',
  },
  label: {
    fontSize: '13px',
    fontWeight: '600',
    color: THEME.colors.textMuted,
    marginBottom: '8px',
    display: 'block',
  },
};

const TYPE_META = {
  [BRIDGE_TYPES.MARKET_ANALYSIS]:      { emoji: '📊', desc: 'Deep ICT/SMC analysis with OBs, FVGs, liquidity' },
  [BRIDGE_TYPES.DAILY_LEARNING]:       { emoji: '📚', desc: 'Process today\'s recap — JARVIS learns behaviors' },
  [BRIDGE_TYPES.KNOWLEDGE_EXTRACTION]: { emoji: '🧠', desc: 'Extract rules from articles or chart observations' },
  [BRIDGE_TYPES.TRADE_REVIEW]:         { emoji: '🔍', desc: 'Deep review of a specific trade — win or loss' },
  [BRIDGE_TYPES.WEEKLY_BRIEFING]:      { emoji: '📅', desc: 'Monday morning — full week analysis + next week plan' },
};

export default function ClaudeBridge() {
  const [step, setStep]           = useState(1); // 1=select type, 2=generate prompt, 3=paste & learn
  const [bridgeType, setBridgeType] = useState(BRIDGE_TYPES.MARKET_ANALYSIS);
  const [question, setQuestion]   = useState('');
  const [generatedPrompt, setGeneratedPrompt] = useState('');
  const [pastedResponse, setPastedResponse]   = useState('');
  const [result, setResult]       = useState(null);
  const [error, setError]         = useState('');
  const [copied, setCopied]       = useState(false);
  const promptRef = useRef(null);

  const handleGenerate = () => {
    setError('');
    try {
      const prompt = generateBridgePrompt(bridgeType, { question });
      setGeneratedPrompt(prompt);
      setStep(2);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(generatedPrompt);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      if (promptRef.current) {
        promptRef.current.select();
        document.execCommand('copy');
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    }
  };

  const handleProcess = () => {
    if (!pastedResponse.trim()) {
      setError('Paste Claude\'s response first.');
      return;
    }
    setError('');
    try {
      const res = processBridgeResponse(bridgeType, pastedResponse);
      setResult(res);
      setStep(3);
    } catch (err) {
      setError('Processing failed: ' + err.message);
    }
  };

  const handleReset = () => {
    setStep(1);
    setGeneratedPrompt('');
    setPastedResponse('');
    setResult(null);
    setError('');
    setQuestion('');
  };

  return (
    <div style={S.container}>
      {/* Header */}
      <div style={S.header}>
        <span style={{ fontSize: '32px' }}>🌉</span>
        <div>
          <div style={S.title}>CLAUDE BRIDGE</div>
          <div style={S.subtitle}>Generate prompts → paste to claude.ai → JARVIS learns automatically</div>
        </div>
      </div>

      {/* Step indicator */}
      <div style={S.stepRow}>
        {['1. Choose Type', '2. Copy & Paste', '3. JARVIS Learns'].map((label, i) => (
          <div key={i} style={S.step(step === i + 1)}>
            {label}
          </div>
        ))}
      </div>

      {error && <div style={S.errorBanner}>⚠️ {error}</div>}

      {/* ── Step 1: Select type ── */}
      {step === 1 && (
        <>
          <div style={S.section}>
            <div style={S.sectionTitle}>What kind of analysis do you need?</div>
            <div style={S.typeGrid}>
              {Object.entries(BRIDGE_TYPE_LABELS).map(([type, label]) => (
                <button
                  key={type}
                  style={S.typeBtn(bridgeType === type)}
                  onClick={() => setBridgeType(type)}
                >
                  <span style={S.typeEmoji}>{TYPE_META[type]?.emoji}</span>
                  {label}
                  <div style={{ fontSize: '11px', color: THEME.colors.textMuted, marginTop: '4px', fontWeight: 400 }}>
                    {TYPE_META[type]?.desc}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {bridgeType === BRIDGE_TYPES.MARKET_ANALYSIS && (
            <div style={S.section}>
              <label style={S.label}>Your specific question (optional)</label>
              <textarea
                style={S.input}
                placeholder="e.g. Is there a valid OB setup for London session tomorrow? What's the H4 bias?"
                value={question}
                onChange={e => setQuestion(e.target.value)}
              />
            </div>
          )}

          <div style={S.btnRow}>
            <button style={S.btn('primary')} onClick={handleGenerate}>
              ⚡ Generate Prompt
            </button>
          </div>
        </>
      )}

      {/* ── Step 2: Generated prompt + paste response ── */}
      {step === 2 && (
        <>
          <div style={S.section}>
            <div style={S.sectionTitle}>
              Step 1 — Copy this prompt → open claude.ai → paste it
            </div>
            <textarea
              ref={promptRef}
              style={S.promptBox}
              value={generatedPrompt}
              readOnly
            />
            <p style={S.hint}>
              💡 This prompt contains your full trading context so Claude gives JARVIS-aware analysis.
            </p>
            <div style={{ ...S.btnRow, marginTop: '12px' }}>
              <button style={S.btn('primary')} onClick={handleCopy}>
                {copied ? '✅ Copied!' : '📋 Copy Prompt'}
              </button>
              <a
                href="https://claude.ai"
                target="_blank"
                rel="noreferrer"
                style={{ ...S.btn('ghost'), textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                🌐 Open claude.ai
              </a>
            </div>
          </div>

          <div style={S.section}>
            <div style={S.sectionTitle}>
              Step 2 — Paste Claude's response here
            </div>
            <textarea
              style={S.pasteBox}
              placeholder="Paste Claude's full response here..."
              value={pastedResponse}
              onChange={e => setPastedResponse(e.target.value)}
            />
            <p style={S.hint}>
              🧠 JARVIS will automatically extract rules, behaviors, and patterns from the response.
            </p>
            <div style={{ ...S.btnRow, marginTop: '12px' }}>
              <button
                style={{ ...S.btn('success'), opacity: pastedResponse.trim() ? 1 : 0.5 }}
                onClick={handleProcess}
                disabled={!pastedResponse.trim()}
              >
                🧠 Process & Learn
              </button>
              <button style={S.btn('ghost')} onClick={handleReset}>
                ↩ Start Over
              </button>
            </div>
          </div>
        </>
      )}

      {/* ── Step 3: Learning results ── */}
      {step === 3 && result && (
        <>
          <div style={S.savedBanner}>
            <div style={S.savedTitle}>
              {result.total > 0 ? `✅ JARVIS learned ${result.total} new item${result.total > 1 ? 's' : ''}!` : '✅ Response processed'}
            </div>
            <div style={S.savedDetail}>{result.summary}</div>

            {result.total > 0 && (
              <div style={S.savedPills}>
                {result.saved.rules > 0 && (
                  <span style={S.pill(THEME.colors.blue)}>{result.saved.rules} Rule{result.saved.rules > 1 ? 's' : ''}</span>
                )}
                {result.saved.behaviors > 0 && (
                  <span style={S.pill(THEME.colors.gold)}>{result.saved.behaviors} Behavior{result.saved.behaviors > 1 ? 's' : ''}</span>
                )}
                {result.saved.patterns > 0 && (
                  <span style={S.pill(THEME.colors.green)}>{result.saved.patterns} Pattern{result.saved.patterns > 1 ? 's' : ''}</span>
                )}
                {result.saved.lessons > 0 && (
                  <span style={S.pill('#9b59b6')}>{result.saved.lessons} Lesson{result.saved.lessons > 1 ? 's' : ''}</span>
                )}
              </div>
            )}
          </div>

          {/* Show Claude's analysis summary if available */}
          {result.data?.jarvis_insights && (
            <div style={S.section}>
              <div style={S.sectionTitle}>JARVIS Insights</div>
              <div style={{ fontSize: '14px', color: THEME.colors.text, lineHeight: '1.7' }}>
                {result.data.jarvis_insights}
              </div>
            </div>
          )}
          {result.data?.message_to_sir && (
            <div style={{ ...S.section, borderColor: `rgba(255,215,0,0.3)` }}>
              <div style={{ ...S.sectionTitle, color: THEME.colors.gold }}>JARVIS to Sir</div>
              <div style={{ fontSize: '14px', color: THEME.colors.text, lineHeight: '1.7', fontStyle: 'italic' }}>
                "{result.data.message_to_sir}"
              </div>
            </div>
          )}
          {result.data?.tomorrow_watch && (
            <div style={S.section}>
              <div style={S.sectionTitle}>Tomorrow — Watch For</div>
              <div style={{ fontSize: '14px', color: THEME.colors.text, lineHeight: '1.7' }}>
                {result.data.tomorrow_watch}
              </div>
            </div>
          )}

          <div style={S.btnRow}>
            <button style={S.btn('primary')} onClick={handleReset}>
              + New Bridge Session
            </button>
          </div>
        </>
      )}
    </div>
  );
}

